import * as React from "react";

import { DateRangePicker } from "@bjarkehs/react-nice-dates";
import { faBan, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { enAU } from "date-fns/locale";
import _ from "lodash";
import { DateTime } from "luxon";
import { Alert, Button, Card, Group, TextInput, Tooltip } from "@mantine/core";
import AvailabilitiesApi from "../../data/api/availabilitiesApi";
import { IAssociation } from "../../types/association/Association";
import { IAvailability } from "../../types/availabilities/Availabilities";
import { IPersonLite } from "../../types/persons/person";
import { toDateTime } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";
import {
  AvailabilityDateElement,
  AvailabilityDurationElement,
  rowColourFromAvailability,
} from "../profile/AvailabilityHistory";
import { DateRangeFocus } from "./CreateAvailabilityForm";

const iconTick = <FontAwesomeIcon icon={faCheck} />
const iconCross = <FontAwesomeIcon icon={faBan} />

function getAvailIcon(available: boolean, someId: string) {
  const icon = available ? iconTick : iconCross;
  const fullId = `icon-is-avail-${someId}`

  return (
    <Tooltip label={available ? "Available" : "Unavailable"} position="bottom">
      <span id={fullId}>{icon}</span>
    </Tooltip>
  );
}

export interface ICustomAvailabilityDashboardProps {
  association: IAssociation;
}

export interface ICustomAvailabilityDashboardState {
  selectedTitle: string | null;
}

export const AvailabilitiesOrgDashboard: React.FunctionComponent<
  ICustomAvailabilityDashboardProps
> = (props) => {
  const { association: selectedAssociation } = props;
  
  const [orgAvails, setOrgAvails] = React.useState<IAvailability[]>();
  const [persons, setPersons] = React.useState<{ [id: string]: IPersonLite }>(
    {},
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | undefined | null>();

  // const dateFocus = React.useState<string | null>(null)
  const [start, setStart] = React.useState<DateTime>(
    DateTime.now().startOf("month"),
  );
  const [finish, setFinish] = React.useState<DateTime>(DateTime.now());

  // get log
  React.useEffect(() => {
    if (!selectedAssociation) {
      return;
    }
    setLoading(true);
    AvailabilitiesApi.getAvailabilityLogForAssociation(
      selectedAssociation._id,
      start,
      finish,
    ).then((resp) => {
      setLoading(false);
      setOrgAvails(resp.data);
      setPersons(_.keyBy(resp.persons || [], "_id"));
    })
      .catch(er => {
        setError(er.message)
        setLoading(false)
      });
  }, [selectedAssociation, start, finish]);

  if (!selectedAssociation) {
    return (
      <Alert color="blue">
        <h5>Select an Association</h5>
        <p>Please select an association from the dropdown above to view availabilities.</p>
      </Alert>
    );
  }

  return (
    <div className="p-4">
      <h3>Organisational availabilities</h3>
      {start && finish && start >= finish && (
        <Alert color="red">Start date is greater than end date</Alert>
      )}
      <DateRangePicker
        startDate={start.toJSDate()}
        endDate={finish.toJSDate()}
        onStartDateChange={(s) => setStart(toDateTime(s!))}
        onEndDateChange={(f) => setFinish(toDateTime(f!))}
        locale={enAU}
        modifiers={{
          disabled: (date) => date > new Date(),
        }}
      >
        {({ startDateInputProps, endDateInputProps, focus }) => (
          <div className="date-range">
            <p>Start</p>
            <Group grow>
              <TextInput
                className={`input${focus === DateRangeFocus.startDate ? " -focused" : ""
                  }`}
                {...startDateInputProps}
                placeholder="Start date"
              />
              <TextInput
                className={`input${focus === DateRangeFocus.endDate ? " -focused" : ""
                  }`}
                {...endDateInputProps}
                placeholder="End date"
              />
            </Group>
          </div>
        )}
      </DateRangePicker>
      
      {/* Date Range Shortcuts */}
      <div className="mt-3 mb-3">
        <small className="text-muted d-block mb-2">Quick date ranges:</small>
        <div className="d-flex gap-3 flex-wrap">
          <button 
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => {
              const now = DateTime.now();
              setStart(now.startOf('week'));
              setFinish(now.endOf('week'));
            }}
          >
            This Week
          </button>
          <button 
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => {
              const now = DateTime.now();
              setStart(now.startOf('month'));
              setFinish(now.endOf('month'));
            }}
          >
            {DateTime.now().toFormat('MMMM')}
          </button>
          <button 
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => {
              const now = DateTime.now();
              setStart(now.minus({ months: 3 }).startOf('month'));
              setFinish(now.endOf('month'));
            }}
          >
            Last 3 Months
          </button>
          <button 
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => {
              const now = DateTime.now();
              setStart(now.startOf('year'));
              setFinish(now.endOf('year'));
            }}
          >
            This Year
          </button>
        </div>
      </div>
      {error && <Alert color="red">{error}</Alert>}
      {loading && <CenteredLoader />}

      {orgAvails && (
        <SSTable striped={false}>
          <tbody>
            {orgAvails.map((avail) => {
              let comment;

              const rowStyle = { backgroundColor: rowColourFromAvailability(avail) }

              if (avail.isRecurring) {
                // set by the server as an exceptional case
                // @ts-ignore
                comment = avail?.recurrence?.text;
              } else if (avail?.reason) {
                comment = `Custom availability (${avail?.reason})`;
              }

              return (
                <tr
                  key={avail._id}
                  style={rowStyle}
                >
                  <td style={rowStyle}>
                    {getAvailIcon(avail.available, avail._id)}
                  </td>
                  <td style={rowStyle}>
                    {toDateTime(avail.timestamp).toLocaleString(
                      DateTime.DATETIME_SHORT,
                    )}
                  </td>
                  <td style={rowStyle}>{persons[avail.person]?.fullName}</td>
                  <td style={rowStyle}>
                    <AvailabilityDateElement availability={avail} />
                  </td>
                  <td style={rowStyle}>
                    <AvailabilityDurationElement availability={avail} />
                  </td>
                  <td style={rowStyle}>{comment}</td>
                </tr>
              );
            })}
          </tbody>
        </SSTable>
      )}
    </div>
  );
};
